import { useInfiniteQuery } from '@tanstack/react-query';
import { api, type ChatMessageSearchHitResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  chatSearchErrorKind,
  chatSearchFiltersKey,
  isSearchTooBroadError,
  normalizeChatSearchInput,
  type ChatSearchErrorKind,
  type ChatSearchFilters,
} from './useChatMessageSearch';

export interface UseChatGlobalSearchResult {
  results: ChatMessageSearchHitResponse[];
  /** Matches from the first page, counted up to the server cap. */
  total: number;
  /** True when more matches exist than `total` reports (render as "N+"). */
  totalCapped: boolean;
  isLoading: boolean;
  isError: boolean;
  /** The server gave up because the search matched too broadly (422 SEARCH_TOO_BROAD). */
  isTooBroad: boolean;
  /** Why the search failed, when it did; null otherwise. */
  errorKind: ChatSearchErrorKind | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

/**
 * Search across every conversation the user is in, cursor-paginated. Backed by
 * `GET /arches/chat/messages/search`. Same filter rules as
 * {@link useChatMessageSearch} — text below the mode's minimum length is treated as
 * absent, and a sender or content filter alone is a valid search — so the two
 * surfaces behave identically apart from scope.
 *
 * Only runs while `enabled` and at least one selector is usable. The caller is
 * responsible for debouncing `query`.
 */
export function useChatGlobalSearch(
  query: string,
  filters: ChatSearchFilters,
  enabled: boolean,
  pageSize = 25,
): UseChatGlobalSearchResult {
  const { terms, request, isQueryable } = normalizeChatSearchInput(query, filters);

  const result = useInfiniteQuery({
    queryKey: queryKeys.chat.globalMessageSearch(terms ?? '', chatSearchFiltersKey(request)),
    queryFn: ({ pageParam }) =>
      api.searchAllChatMessages(terms ?? '', {
        ...request,
        limit: pageSize,
        before: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && isQueryable,
    staleTime: 30_000,
  });

  const firstPage = result.data?.pages[0];
  return {
    results: result.data?.pages.flatMap((p) => p.messages) ?? [],
    total: firstPage?.total ?? 0,
    totalCapped: firstPage?.totalCapped ?? false,
    isLoading: isQueryable && result.isLoading,
    isError: result.isError,
    isTooBroad: isSearchTooBroadError(result.error),
    errorKind: chatSearchErrorKind(result.error, request),
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: () => void result.fetchNextPage(),
  };
}
